import { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../services/api";
import { getBackendUrl } from "../utils/backendUrl";
import { Upload, Search, X, Loader2 } from "lucide-react";
import Button from "./Button";
import Input from "./Input";

export default function MediaSelector({ onSelect, onClose }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedCollection, setSelectedCollection] = useState("");
  const [view, setView] = useState("browse");
  const [uploading, setUploading] = useState(false);
  const [uploadCollection, setUploadCollection] = useState("");
  const [uploadVisibility, setUploadVisibility] = useState("public");
  const fileInputRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ["media", searchTerm, selectedType, selectedCollection],
    queryFn: () =>
      api
        .get("/media", {
          params: {
            search: searchTerm || undefined,
            type: selectedType || undefined,
            collection_id: selectedCollection || undefined,
            per_page: 50,
          },
        })
        .then((res) => res.data),
  });

  const { data: collectionsData } = useQuery({
    queryKey: ["media-collections"],
    queryFn: () => api.get("/media/collections/list").then((res) => res.data),
  });

  const media = data?.data || [];
  const collections = collectionsData?.data || [];

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("visibility", uploadVisibility);
    if (uploadCollection) {
      formData.append("collection_ids[]", uploadCollection);
    }

    try {
      const response = await api.post("/media/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const item = response.data.data;
      const payload = {
        id: item.id,
        url: item.preferred_url || item.url,
        width: item.width || "",
        height: item.height || "",
        alt_text: item.alt_text || "",
      };
      if (onSelect.length >= 2) {
        onSelect(payload.url, payload);
      } else {
        onSelect(payload);
      }
      onClose();
    } catch (error) {
      alert("Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1024 / 1024).toFixed(1) + " MB";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Select Media</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex border-b">
          <button
            onClick={() => setView("browse")}
            className={`px-4 py-2 font-medium ${
              view === "browse"
                ? "border-b-2 border-primary-600 text-primary-600"
                : "text-gray-600"
            }`}
          >
            Browse Library
          </button>
          <button
            onClick={() => setView("upload")}
            className={`px-4 py-2 font-medium ${
              view === "upload"
                ? "border-b-2 border-primary-600 text-primary-600"
                : "text-gray-600"
            }`}
          >
            Upload New
          </button>
        </div>

        {view === "upload" ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center space-y-4">
              <Upload className="h-16 w-16 text-gray-400 mx-auto" />
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleUpload}
                  className="hidden"
                  disabled={uploading}
                />
                <Button
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Choose File
                    </>
                  )}
                </Button>
                <select
                  value={uploadVisibility}
                  onChange={(e) => setUploadVisibility(e.target.value)}
                  className="mt-3 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
                <select
                  value={uploadCollection}
                  onChange={(e) => setUploadCollection(e.target.value)}
                  className="mt-3 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">No Collection</option>
                  {collections.map((collection) => (
                    <option key={collection.id} value={collection.id}>
                      {collection.name}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-sm text-gray-500">
                Upload a new image or video file
              </p>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="p-4 border-b space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search media..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">All Types</option>
                  <option value="image">Images</option>
                  <option value="video">Videos</option>
                </select>
                <select
                  value={selectedCollection}
                  onChange={(e) => setSelectedCollection(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">All Collections</option>
                  {collections.map((collection) => (
                    <option key={collection.id} value={collection.id}>
                      {collection.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-4">
              {isLoading ? (
                <div className="text-center py-12">
                  <div className="text-gray-500">Loading media...</div>
                </div>
              ) : media.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">No media found</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {media.map((item) => (
                    <div
                      key={item.id}
                      className="relative group cursor-pointer"
                      onClick={() => {
                        const payload = {
                          id: item.id,
                          url: item.preferred_url || item.url,
                          width: item.width || "",
                          height: item.height || "",
                          alt_text: item.alt_text || "",
                        };
                        if (onSelect.length >= 2) {
                          onSelect(payload.url, payload);
                        } else {
                          onSelect(payload);
                        }
                        onClose();
                      }}
                    >
                      <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                        {item.type === "image" ? (
                          <img
                            src={item.url.startsWith("http") ? item.url : `${getBackendUrl() || ""}${item.url}`}
                            alt={item.alt_text || item.original_filename}
                            className="w-full h-full object-cover group-hover:scale-105 transition"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-200">
                            <video className="w-full h-full object-cover">
                              <source src={item.url.startsWith("http") ? item.url : `${getBackendUrl() || ""}${item.url}`} type={item.mime_type} />
                            </video>
                          </div>
                        )}
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition rounded-lg flex items-center justify-center">
                        <span className="opacity-0 group-hover:opacity-100 text-white text-sm font-medium">
                          Select
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
